import { LayoutDashboard, Send, Users, FileText, Settings, Shield, CreditCard, UserPlus, BarChart3 } from "lucide-react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { useCallback } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useOrgRoutePrefetch } from "@/hooks/useOrgRoutePrefetch";
import { useAuth } from "@/supabase-auth";
import { SidebarProfileFooter, SettingsMenuItem } from "@/components/sidebar/SidebarProfileFooter";
import oneFormIconDark from "@/assets/oneform-icon-dark.svg";
import oneFormIconLight from "@/assets/oneform-icon-light.svg";
import { ORG_ROUTES } from "@/routes/routes";

const navItems = [
  { title: "Dashboard", url: ORG_ROUTES.dashboard, icon: LayoutDashboard },
  { title: "Submissions", url: ORG_ROUTES.submissions, icon: Users },
  { title: "Invitations", url: ORG_ROUTES.invitations, icon: Send },
  { title: "Templates", url: ORG_ROUTES.templates, icon: FileText },
  { title: "Team", url: ORG_ROUTES.team, icon: UserPlus },
  { title: "Analytics", url: ORG_ROUTES.analytics, icon: BarChart3 },
  { title: "Audit Trail", url: ORG_ROUTES.audit, icon: Shield },
];

// Settings items for the sidebar footer flyout
const settingsItems: SettingsMenuItem[] = [
  { title: "Org Billing", url: ORG_ROUTES.billing, icon: CreditCard },
  { title: "Org Settings", url: ORG_ROUTES.settings, icon: Settings },
];

export function OrgSidebar() {
  const { open } = useSidebar();
  const location = useLocation();
  const { prefetchRoute } = useOrgRoutePrefetch();
  const { session, profile } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  // Prefetch route data on hover for faster navigation
  const handleMouseEnter = useCallback(
    (url: string) => {
      prefetchRoute(url);
    },
    [prefetchRoute]
  );

  // Prepare user data for profile footer
  const fullName = profile?.full_name || session?.user?.email?.split('@')[0] || 'User';
  const email = profile?.email || session?.user?.email || '';
  const avatarUrl = profile?.avatar_url;

  return (
    <Sidebar collapsible="icon" id="navigation" aria-label="Organization navigation" className="flex h-dvh flex-col">
      <SidebarHeader className="border-b">
        <Link to={ORG_ROUTES.dashboard} className="flex items-center gap-2 px-4 py-3">
          <img src={oneFormIconDark} alt="OneForm" className="w-6 h-6 dark:hidden flex-shrink-0" />
          <img src={oneFormIconLight} alt="OneForm" className="w-6 h-6 hidden dark:block flex-shrink-0" />
          {open && <span className="font-semibold">OneForm</span>}
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Organization</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <NavLink 
                      to={item.url} 
                      end
                      onMouseEnter={() => handleMouseEnter(item.url)}
                    >
                      <item.icon className="h-4 w-4" />
                      {open && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      {/* Profile footer pinned to bottom */}
      <div className="mt-auto px-3 pb-3">
        <SidebarProfileFooter
          fullName={fullName}
          email={email}
          avatarUrl={avatarUrl}
          settingsItems={settingsItems}
        />
      </div>
    </Sidebar>
  );
}
